<template>
  <div :class="[$style.config, $style.wrap]">
    <AspectRatio
      v-if="data?.fields?.poster?.fields?.file?.url"
      ratio="1920x1080"
    >
      <BasePicture
        :src="data?.fields?.poster?.fields?.file?.url"
        :width="data?.fields?.poster?.fields?.file?.details?.image?.width"
        :height="data?.fields?.poster?.fields?.file?.details?.image?.height"
        alt=""
      />
    </AspectRatio>
    <div :class="$style.content">
      <div :class="$style.meta">
        <div :class="$style.type">
          <IconMic v-if="instanceOfPodcast(data)" :class="$style.icon" />
          <IconPlayNegative
            v-if="instanceOfVideoContent(data)"
            :class="$style.icon"
          />
          <span :style="cssOverride as StyleValue" :class="$style.label">
            {{ data?.fields?.mediaType?.fields?.label }}
          </span>
        </div>

        <time :datetime="data?.fields?.dateTime" :class="$style.date">
          {{ data?.fields?.date }}
        </time>
      </div>

      <h4 :class="$style.title">
        {{ data?.fields?.title }}
      </h4>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypePodcastWithoutUnresolvableLinksResponse,
  TypeVideoContentWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { StyleValue } from 'vue'

const props = defineProps<{
  data:
    | TypePodcastWithoutUnresolvableLinksResponse
    | TypeVideoContentWithoutUnresolvableLinksResponse
}>()

const cssOverride = instanceOfPodcast(props.data)
  ? props.data?.fields?.cssOverride
  : {}
</script>

<style lang="scss" module>
.config {
  --playlist-background: #f2f5fa;
  --playlist-date-color: #667080;

  @include breakpoint-variables(
    '--poster-size',
    (
      base: 164px,
      sm: 200px,
    )
  );

  @include breakpoint-variables(
    '--title-font-weight',
    (
      base: 600,
      sm: 700,
    )
  );
}

.wrap {
  display: grid;
  position: relative;
  grid-template-columns: var(--poster-size) 1fr;
  width: 100%;

  &.active::before {
    pointer-events: none;
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid var(--theme-accent-color);
    z-index: 1;
  }
}

.content {
  @include fluid-type(12px, 16px);
  background: var(--playlist-background);
  padding: 1em;
  flex: 1;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.type {
  display: flex;
  align-items: center;
}

.icon {
  width: 1.25em;
  height: 1.25em;
}

.label {
  display: inline-block;

  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;

  margin: 0 0.5em;
  color: var(
    --label-color,
    var(--black)
  ); // this var is set in contentful, don't change!
}

.date {
  color: var(--playlist-date-color);
  font-weight: bold;
  font-size: 12px;
  display: none;

  @include media('xs') {
    display: block;
  }
}

.title {
  @include fluid-type(13px, 16px);
  font-family: var(--base-font-family);
  font-weight: var(--title-font-weight);
  text-transform: none;

  margin: 0.5em 0;

  display: -webkit-box;
  word-break: break-word;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
